import { suiteRoot } from "./auth";

export type MailAccount = { id: number; email: string; displayName?: string; isDefault?: boolean };
export type MailHit = { accountId: number; uid: number; subject: string; from: string; date: string };
export type MailBody = { subject: string; from: string; date: string; html: string; text: string };

async function mailFetch(path: string, body?: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(`${suiteRoot()}/mail/api_mail/${path}`, {
    method: body ? "POST" : "GET",
    credentials: "include",
    cache: "no-store",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Mail ne répond pas."));
  }
  return data;
}

export async function listMailAccounts(): Promise<MailAccount[]> {
  const data = await mailFetch("accounts/list.php");
  const rows = Array.isArray(data.accounts) ? data.accounts : [];
  return rows.map((row) => {
    const item = row as { id?: number; email?: string; displayName?: string; isDefault?: boolean };
    return { id: Number(item.id) || 0, email: String(item.email || ""), displayName: item.displayName, isDefault: item.isDefault };
  }).filter((item) => item.id > 0 && item.email !== "");
}

export async function listClientInboxes(emails: string[]): Promise<MailHit[]> {
  const unique = [...new Set(emails.map((item) => item.trim().toLowerCase()).filter((item) => item.includes("@")))];
  if (unique.length === 0) return [];
  const packs = await Promise.all(unique.map(async (email) => {
    try {
      return await listClientInbox(email);
    } catch (reason) {
      if (unique.length === 1) throw reason;
      return [];
    }
  }));
  const seen = new Set<string>();
  return packs.flat().filter((hit) => {
    const key = `${hit.accountId}-${hit.uid}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
}

export async function listClientInbox(email: string): Promise<MailHit[]> {
  const target = email.trim().toLowerCase();
  if (!target.includes("@")) throw new Error("Renseignez l’e-mail du client sur la fiche.");
  const accounts = await listMailAccounts();
  if (accounts.length === 0) throw new Error("Aucun compte Mail.");
  let failed = 0;
  const packs = await Promise.all(accounts.map(async (account) => {
    try {
      const query = new URLSearchParams({ accountId: String(account.id), folder: "INBOX", q: `from:${target}`, limit: "20" });
      const data = await mailFetch(`imap/messages.php?${query}`);
      const rows = Array.isArray(data.messages) ? data.messages : [];
      return rows.flatMap((row) => {
        const item = row as { uid?: number; subject?: string; from?: string; date?: string };
        const from = String(item.from || "");
        if (!from.toLowerCase().includes(target)) return [];
        return [{ accountId: account.id, uid: Number(item.uid) || 0, subject: item.subject || "(Sans objet)", from, date: String(item.date || "") }];
      });
    } catch {
      failed += 1;
      return [];
    }
  }));
  const hits = packs.flat().filter((item) => item.uid > 0).sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  if (hits.length === 0 && failed === accounts.length) {
    throw new Error("La boîte de réception ne s’ouvre pas. Vérifiez la connexion du compte dans Mail.");
  }
  return hits;
}

export async function readMail(accountId: number, uid: number): Promise<MailBody> {
  const query = new URLSearchParams({ accountId: String(accountId), folder: "INBOX", uid: String(uid) });
  const data = await mailFetch(`imap/message.php?${query}`);
  const message = (data.message || {}) as { subject?: string; from?: string; date?: string; bodyHtml?: string; bodyText?: string };
  return {
    subject: message.subject || "(Sans objet)",
    from: message.from || "",
    date: message.date || "",
    html: message.bodyHtml || "",
    text: message.bodyText || "",
  };
}

export async function sendClientMail(input: { accountId: number; to: string; subject: string; html: string; attachments?: { filename: string; mimeType: string; contentBase64: string }[] }): Promise<void> {
  const settings = await mailFetch(`accounts/settings.php?accountId=${input.accountId}`);
  const rows = Array.isArray(settings.settings) ? settings.settings : [];
  const signature = String((rows[0] as { signature?: string } | undefined)?.signature || "").trim();
  const html = signature
    ? `${input.html}<div data-mail-signature="1" style="margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb;">${signature}</div>`
    : input.html;
  const text = html.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
  await mailFetch("send-message.php", {
    accountId: input.accountId,
    to: input.to,
    subject: input.subject,
    bodyHtml: html,
    bodyText: text,
    attachments: input.attachments,
  });
}
